/**
 * Line Chart Component
 *
 * Reads the same payload shape as the bar resolver but renders an
 * `AreaChart` for the primary series. Adds:
 *  - Optional target reference line (`meta.target_value`)
 *  - SMA overlay line when rows carry an `sma` field
 *  - Secondary right-hand axis when `series.y2` exists
 */

import { useMemo } from 'react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
  ReferenceLine,
} from 'recharts';
import { CHART_COLORS } from '../constants';
import { generateMockData, MockBarData } from '../utils';
import { useIndicatorData, IndicatorDataPoint } from './useIndicatorData';

interface LineChartComponentProps {
  indicatorId?: number;
  height?: number | string;
}

type LineRow = IndicatorDataPoint | MockBarData;

export function LineChartComponent({ indicatorId, height = 250 }: LineChartComponentProps = {}) {
  const { data: live, loading } = useIndicatorData(indicatorId);
  const isPreviewMode = indicatorId === undefined;
  const data: LineRow[] = useMemo(() => {
    if (live && Array.isArray(live.data) && live.data.length > 0) {
      return live.data;
    }
    if (isPreviewMode) return generateMockData('line') as MockBarData[];
    return [];
  }, [live, isPreviewMode]);

  const series = live?.series;
  const meta = live?.meta;
  const targetValue = typeof meta?.target_value === 'number' ? meta.target_value : null;
  const valueLabel = series?.['value']?.label || 'Value';
  const y2Entry = series?.['y2'];
  const hasY2 = Boolean(y2Entry) && data.some((row) => 'y2' in row);
  const hasSMA = Boolean(meta?.show_sma) && data.some((row) => 'sma' in row);

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ height: typeof height === 'number' ? `${height}px` : height }}
      >
        {loading ? 'Loading…' : (live?.message || 'No data. Configure axes in the builder.')}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <defs>
          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={CHART_COLORS[0]} stopOpacity={0.3} />
            <stop offset="95%" stopColor={CHART_COLORS[0]} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" fontSize={12} />
        <YAxis yAxisId="left" fontSize={12} />
        {hasY2 && <YAxis yAxisId="right" orientation="right" fontSize={12} />}
        <Tooltip />
        <Legend verticalAlign="top" height={28} wrapperStyle={{ paddingBottom: 4 }} />
        <Area
          yAxisId="left"
          type="monotone"
          dataKey="value"
          stroke={CHART_COLORS[0]}
          fill="url(#colorValue)"
          name={valueLabel}
        />
        {!targetValue && data.some((row) => 'target' in row && row.target !== undefined) && (
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="target"
            stroke={CHART_COLORS[1]}
            strokeDasharray="5 5"
            name="Target"
            dot={false}
          />
        )}
        {targetValue !== null && (
          <ReferenceLine
            yAxisId="left"
            y={targetValue}
            stroke={CHART_COLORS[3]}
            strokeDasharray="4 4"
            label={{ value: `Target: ${targetValue}`, position: 'insideTopRight', fill: CHART_COLORS[3], fontSize: 11 }}
          />
        )}
        {hasSMA && (
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="sma"
            stroke={CHART_COLORS[4]}
            strokeWidth={2}
            dot={false}
            name="SMA"
          />
        )}
        {hasY2 && (
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="y2"
            stroke={CHART_COLORS[2]}
            strokeWidth={2}
            dot={false}
            name={y2Entry?.label || 'Series 2'}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
